import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Container,
} from "@mui/material";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { theme } from "../../../Theme/index";
import { getUrl } from "../../../Utils/Common";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import DateRangePicker from "src/Components/Charts/ActualVsExpectedMixChart/DatePicker";
import dayjs from "dayjs";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "center",
  borderBottom: "1px solid #e0e0e0",
  color: theme.palette.primary.dark,
  fontSize: "12px",
  [theme.breakpoints.down('lg')]: {
    padding: "6px 8px",
    fontSize: "11px",
  },
  [theme.breakpoints.down('md')]: {
    padding: "4px 6px",
    fontSize: "10px",
  },
  [theme.breakpoints.down('sm')]: {
    padding: "2px 4px",
    fontSize: "8px",
  },
}));

const LabelCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "left",
  borderBottom: "1px solid #e0e0e0",
  color: "white",
  fontSize: "12px",
  minWidth: "120px",
  maxWidth: "150px",
  [theme.breakpoints.down('lg')]: {
    padding: "6px 8px",
    fontSize: "11px",
    minWidth: "100px",
    maxWidth: "120px",
  },
  [theme.breakpoints.down('md')]: {
    padding: "4px 6px",
    fontSize: "10px",
    minWidth: "80px",
    maxWidth: "100px",
  },
  [theme.breakpoints.down('sm')]: {
    padding: "2px 4px",
    fontSize: "8px",
    minWidth: "50px",
    maxWidth: "70px",
  },
}));


const NewTable = ({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  startDate: propStartDate,
  setStartDate: propSetStartDate,
  endDate: propEndDate,
  setEndDate: propSetEndDate,
  selectedTab: propSelectedTab,
  setSelectedTab: propSetSelectedTab,
  timePeriod: propTimePeriod,
  setTimePeriod: propSetTimePeriod,
  target: propTarget,
  setTarget: propSetTarget,
  onDataLoaded,
}) => {
  const projectId = useSelector((state) => state.userProject.projectId);

  const [tableData, setTableData] = useState(null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const loading = loadingHierarchy || loadingTable;

  const [error, setError] = useState(null);

  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState( propSelectedHierarchy || "brand" );
  const selectedHierarchy = propSelectedHierarchy !== undefined ? propSelectedHierarchy : localSelectedHierarchy;
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [localStartDate, setLocalStartDate] = useState(dayjs().subtract(2, "year"));
  const startDate = propStartDate !== undefined ? propStartDate : localStartDate;
  const setStartDate = propSetStartDate || setLocalStartDate;

  const [localEndDate, setLocalEndDate] = useState(dayjs().subtract(1, "year"));
  const endDate = propEndDate !== undefined ? propEndDate : localEndDate;
  const setEndDate = propSetEndDate || setLocalEndDate;

  const [localSelectedTab, setLocalSelectedTab] = useState(0);
  const selectedTab = propSelectedTab !== undefined ? propSelectedTab : localSelectedTab;
  const setSelectedTab = propSetSelectedTab || setLocalSelectedTab;

  const [localTimePeriod, setLocalTimePeriod] = useState("months");
  const timePeriod = propTimePeriod !== undefined ? propTimePeriod : localTimePeriod;
  const setTimePeriod = propSetTimePeriod || setLocalTimePeriod;

  const [localTarget, setLocalTarget] = useState(11);
  const target = propTarget !== undefined ? propTarget : localTarget;
  const setTarget = propSetTarget || setLocalTarget;

  const handleHierarchyChange = (newHierarchy) => {
  if (propSetSelectedHierarchy && typeof propSetSelectedHierarchy === 'function') {
    propSetSelectedHierarchy(newHierarchy);
    } else {
      setLocalSelectedHierarchy(newHierarchy);
    }
  };

  const handleRefresh = () => {
    setTableData(null);
    setError(null);
    setLoadingTable(true);
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        return;
      }
      setLoadingHierarchy(true);
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};
          const allowedHierarchyLevels = ["brand", "class", "manufacturer", "segment"];
          const rawHierarchyLevels = Object.keys(hierarchyData);
          const hierarchyLevels = rawHierarchyLevels.filter((key) =>
            allowedHierarchyLevels.includes(key)
          );
          hierarchyLevels.push("channel", "retailer");
          setHierarchyOptions(hierarchyLevels);
          if (!propSelectedHierarchy) {
            setLocalSelectedHierarchy(
              hierarchyLevels.includes("brand") ? "brand" : hierarchyLevels[0] || ""
            );
          }
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
        })
        .finally(() => {
          setLoadingHierarchy(false);
        });
    }
    fetchHierarchyLevels();
  }, [projectId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        return;
      }
      try {
        setLoadingTable(true);
        setError(null);
        const response = await fetch(
          `${getUrl()}/market_overview/growth_contribution_total?project_id=${projectId}&time_period=${timePeriod}&target=${target}&filters={}&hierarchy=${selectedHierarchy}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-access-token": localStorage.getItem("userId") || "",
              "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const apiData = await response.json();
        const transformedData = transformApiData(apiData);
        setTableData(transformedData);
        
        if (onDataLoaded) {
          const flatData = [
            { 
              name: "TOTAL", 
              contribution: transformedData.total.contribution, 
              value_sales: transformedData.total.valueSales, 
              iya: transformedData.total.iya 
            },
            ...transformedData.brands.map(b => ({
              name: b.name,
              contribution: b.contribution,
              value_sales: b.valueSales,
              iya: b.iya
            }))
          ];
          onDataLoaded(flatData);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoadingTable(false);
      }
    };

    fetchData();
  }, [projectId, selectedHierarchy, refreshTrigger, timePeriod, target]);

  const transformApiData = (apiData) => {
    try {
      const totalIndex = 0;
      const hierarchyKey = selectedHierarchy;
      const hierarchyData = apiData[hierarchyKey] || apiData.brand || [];
      const brandsData = hierarchyData.slice(1);
      const brandsValueSales = apiData.value_sales.slice(1);
      const brandsIya = apiData.value_sales_iya.slice(1);
      const brandsContribution = apiData["Contribution to Growth"].slice(1);
      return {
        total: {
          contribution: apiData["Contribution to Growth"][totalIndex] || 0,
          valueSales: apiData.value_sales[totalIndex] || 0,
          iya: apiData.value_sales_iya[totalIndex] || 0,
        },
        brands: brandsData.map((brandName, index) => ({
          name: brandName,
          contribution: brandsContribution[index] || 0,
          valueSales: brandsValueSales[index] || 0,
          iya: brandsIya[index] || 0,
        })),
      };
    } catch (err) {
      console.error("Error transforming API data:", err);
      return tableData;
    }
  };
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "70%", sm: "90%", md: "95%", lg: "100%" },
        margin: "0 auto",
        padding: { xs: "1px", sm: "2px", md: "4px" },
        overflow: "auto",
      }}
    >
      <Paper elevation={0} sx={{ overflow: "visible", minHeight: "auto" }}>
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: {
              xs: "0.9rem",
              sm: "1.2rem",
              md: "1.3rem",
            },
            px: {
              xs: 0.5,
              sm: 0.75,
              md: 1,
            },
            color: theme.palette.primary.main,
            textAlign: "center",
            m: 0,
            lineHeight: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 48,
          }}
        >
          Market Growth
        </Typography>

        {/* Date Range Picker */}
        <Container
          sx={{
            padding: { xs: "0 2px", sm: "0 4px", md: "0 8px" },
            maxWidth: "100%",
          }}
        >
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setTimePeriod={setTimePeriod}
            setTarget={setTarget}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            variant="default"
          />
        </Container>

        <Box
          display="flex"
          justifyContent="center"
          mb={1}
          gap={{ xs: 1, sm: 2 }}
          px={{ xs: 1, sm: 2 }}
        >
          <CustomSelectBox
            sx={{
              align: "center",
              minWidth: { xs: "100px", sm: "140px", md: "160px" },
              fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
            }}
            value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
            onChange={(event) => {
              handleHierarchyChange(event.target.value);
            }}
            options={hierarchyOptions}
          />
        </Box>
        {/* Data Table Section */}
        <TableContainer
          sx={{
            maxHeight: "none",
            overflow: "auto",
            overflowX: "auto",
            padding: { xs: "1px", sm: "2px", md: "4px" },
            width: "100%",
          }}
        >
          {loading || !tableData ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <FacebookCircularProgress loading={true} hasData={true} />
            </Box>
          ) : error || !tableData.brands || tableData.brands.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <FacebookCircularProgress
                loading={false}
                hasData={false}
                onRefresh={handleRefresh}
                message={
                  error
                    ? `Error loading data: ${error} - Click to retry`
                    : "No data available - Click to reset chart"
                }
              />
            </Box>
          ) : (
            (() => {
              const contributions = [
                tableData.total.contribution,
                ...tableData.brands.map((b) => b.contribution),
              ];
              const maxPositive = Math.max(
                0,
                ...contributions.filter((v) => v > 0)
              );
              const maxNegative = Math.max(
                0,
                ...contributions.filter((v) => v < 0).map((v) => Math.abs(v))
              );
              const chartHeight = 150;
              const totalRange = maxPositive + maxNegative || 1;
              const positiveHeight = (maxPositive / totalRange) * chartHeight;
              const negativeHeight = (maxNegative / totalRange) * chartHeight;

              return (
                <Table
                  sx={{
                    minWidth: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                  }}
                >
                  <TableHead>
                    {/* Chart Row with Vertical Title */}
                    <TableRow>
                      <StyledTableCell
                        rowSpan={3}
                        sx={{
                          writingMode: "vertical-rl",
                          transform: "rotate(180deg)",
                          textAlign: "center",
                          backgroundColor: "#F8F9FA",
                          color: theme.palette.primary.dark,
                          fontWeight: "bold",
                          fontSize: { xs: "10px", sm: "12px", md: "14px" },
                          minWidth: { xs: "25px", sm: "30px", md: "40px" },
                          maxWidth: { xs: "25px", sm: "30px", md: "40px" },
                          padding: { xs: "4px", sm: "6px", md: "8px" },
                        }}
                      >
                        Market growth contributors
                      </StyledTableCell>

                      {/* ALL Contributions (TOTAL + Brands) */}
                      {[tableData.total, ...tableData.brands].map(
                        (item, index) => {
                          const barHeight =
                            (Math.abs(item.contribution) /
                              (item.contribution > 0
                                ? maxPositive
                                : maxNegative)) *
                            (item.contribution > 0
                              ? positiveHeight
                              : negativeHeight);

                          return (
                            <StyledTableCell
                              key={index}
                              sx={{
                                minWidth: {
                                  xs: "25px",
                                  sm: "35px",
                                  md: "40px",
                                  lg: "45px",
                                },
                                maxWidth: {
                                  xs: "25px",
                                  sm: "35px",
                                  md: "40px",
                                  lg: "45px",
                                },
                                border: "none",
                                padding: {
                                  xs: "0.5px",
                                  sm: "1px",
                                  md: "2px",
                                  lg: "4px",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  textAlign: "center",
                                  height: `${chartHeight}px`,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                }}
                              >
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: `${positiveHeight}px`,
                                    left: "0",
                                    right: "0",
                                    height: "1px",
                                    backgroundColor: "#a7a7a7",
                                    zIndex: 1,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: {
                                      xs: "9px",
                                      sm: "10px",
                                      md: "11px",
                                      lg: "12px",
                                    },
                                    lineHeight: 1.2,
                                    fontWeight: "bold",
                                    zIndex: 3,
                                    backgroundColor: "white",
                                    px: 0.5,
                                    position: "absolute",
                                    bottom:
                                      item.contribution > 0
                                        ? `calc(${negativeHeight}px + ${barHeight}px + 2px)`
                                        : "auto",
                                    top:
                                      item.contribution < 0
                                        ? `calc(${positiveHeight}px + ${barHeight}px + 2px)`
                                        : "auto",
                                  }}
                                >
                                  {Math.round(item.contribution)}%
                                </Typography>
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: {
                                      xs: "20px",
                                      sm: "25px",
                                      md: "28px",
                                      lg: "32px",
                                    },
                                    height: barHeight + "px",
                                    backgroundColor:
                                      item.contribution < 0
                                        ? "#d32f2f"
                                        : "#00265E",
                                    borderRadius:
                                      item.contribution < 0
                                        ? "0 0 4px 4px"
                                        : "4px 4px 0 0",
                                    zIndex: 2,
                                    ...(item.contribution > 0
                                      ? { bottom: `${negativeHeight}px` }
                                      : { top: `${positiveHeight}px` }),
                                  }}
                                />
                              </Box>
                            </StyledTableCell>
                          );
                        }
                      )}
                    </TableRow>

                    {/* Column Names Row */}
                    <TableRow>
                      {[tableData.total, ...tableData.brands].map(
                        (item, index) => (
                          <StyledTableCell
                            key={index}
                            sx={{
                              minWidth: {
                                xs: "25px",
                                sm: "35px",
                                md: "40px",
                                lg: "45px",
                              },
                              maxWidth: { xs: "45px", sm: "55px", md: "60px" },
                              backgroundColor: "#F8F9FA",
                              fontSize: {
                                xs: "8px",
                                sm: "9px",
                                md: "10px",
                                lg: "11px",
                              },
                              padding: { xs: "2px", sm: "4px", md: "6px" },
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                            }}
                          >
                            {index === 0 ? "TOTAL" : item.name}
                          </StyledTableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Value Sales Row */}
                    <TableRow>
                      <LabelCell
                        sx={{
                          backgroundColor: "transparent",
                          minWidth: {
                            xs: "40px",
                            sm: "50px",
                            md: "60px",
                            lg: "70px",
                          },
                          maxWidth: {
                            xs: "50px",
                            sm: "60px",
                            md: "70px",
                            lg: "80px",
                          },
                          color: theme.palette.primary.dark,
                          fontSize: {
                            xs: "8px",
                            sm: "9px",
                            md: "10px",
                            lg: "11px",
                          },
                        }}
                      >
                        Value Sales
                      </LabelCell>
                      {[tableData.total, ...tableData.brands].map(
                        (item, index) => (
                          <StyledTableCell
                            key={index}
                            sx={{
                              minWidth: {
                                xs: "25px",
                                sm: "35px",
                                md: "40px",
                                lg: "45px",
                              },
                              maxWidth: { xs: "45px", sm: "55px", md: "60px" },
                              padding: { xs: "1px", sm: "2px", md: "3px" },
                            }}
                          >
                            <Box sx={{ textAlign: "center" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: {
                                    xs: "8px",
                                    sm: "9px",
                                    md: "10px",
                                    lg: "11px",
                                  },
                                }}
                              >
                                {(item.valueSales / 1000000).toFixed(2)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: {
                                    xs: "7px",
                                    sm: "8px",
                                    md: "9px",
                                    lg: "10px",
                                  },
                                }}
                              >
                                M SAR
                              </Typography>
                            </Box>
                          </StyledTableCell>
                        )
                      )}
                    </TableRow>

                    {/* IYA Row */}
                    <TableRow>
                      <LabelCell
                        sx={{
                          backgroundColor: "transparent",
                          minWidth: {
                            xs: "40px",
                            sm: "50px",
                            md: "60px",
                            lg: "70px",
                          },
                          maxWidth: {
                            xs: "50px",
                            sm: "60px",
                            md: "70px",
                            lg: "80px",
                          },
                          color: theme.palette.primary.dark,
                          fontSize: {
                            xs: "8px",
                            sm: "9px",
                            md: "10px",
                            lg: "11px",
                          },
                        }}
                      >
                        IYA Value Sales
                      </LabelCell>
                      {[tableData.total, ...tableData.brands].map(
                        (item, index) => (
                          <StyledTableCell
                            key={index}
                            sx={{
                              minWidth: {
                                xs: "25px",
                                sm: "35px",
                                md: "40px",
                                lg: "45px",
                              },
                              maxWidth: { xs: "45px", sm: "55px", md: "60px" },
                              padding: { xs: "1px", sm: "2px", md: "3px" },
                              fontSize: {
                                xs: "8px",
                                sm: "9px",
                                md: "10px",
                                lg: "11px",
                              },
                            }}
                          >
                            {Math.round(item.iya)}
                          </StyledTableCell>
                        )
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              );
            })()
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
};

NewTable.propTypes = {
  selectedHierarchy: PropTypes.string,
  setSelectedHierarchy: PropTypes.func,
  startDate: PropTypes.object,
  setStartDate: PropTypes.func,
  endDate: PropTypes.object,
  setEndDate: PropTypes.func,
  selectedTab: PropTypes.number,
  setSelectedTab: PropTypes.func,
  timePeriod: PropTypes.string,
  setTimePeriod: PropTypes.func,
  target: PropTypes.number,
  setTarget: PropTypes.func,
};

NewTable.chartInfo = "IYA (Index Year Ago) Comparison vs. the same period last year";

export default NewTable;
